import React, { useMemo } from 'react';

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from '~/timeInit';

import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import { hasLinkedShare, useShareColour } from './OfferSplit';
import { DailyCostPoint, TourSplit, buildDailyCostPoints } from './dailyCostPoints';
import { ConfigurationOrder, OfferShare, ScalePricingItem, TimelinePoint } from './departureInventory';
import { formatAmount } from './formatAmount';

interface Props {
  timeline: Array<TimelinePoint>;
  currency: string;
  snapshotDate?: number;
  items: Array<ScalePricingItem>;
  season?: string | null;
  showCosts?: boolean;
  /** Who sold the departure's travellers; with an LTLE among them, pax is stacked per tour. */
  shares?: Array<OfferShare>;
  tourOfferId?: string;
  order?: ConfigurationOrder;
}

interface ChartSeries {
  id: string;
  label: string;
  colour: string;
}

/** A closing balance, or the copy of the last one that carries its step to the end of that day. */
type ChartPoint = DailyCostPoint & { day: number };

const AXIS_INK = '#898781';
const GRID = { light: '#e1e0d9', dark: '#2c2c2a' };
const MAX_TICKS = 8;

/** Rates run to eight figures in VND, which no axis can carry in full. */
const compact = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });

function SeriesLabel({ series, value }: { series: ChartSeries; value?: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
      <Box width={8} height={8} borderRadius="50%" bgcolor={series.colour} flexShrink={0} />
      <Typography variant="caption" noWrap title={series.label}>
        {series.label}
      </Typography>
      {value !== undefined && (
        <Typography variant="caption" fontWeight={600} flexShrink={0} sx={{ ml: 'auto', pl: 2 }}>
          {value}
        </Typography>
      )}
    </Stack>
  );
}

function TrendTooltip({
  active,
  payload,
  currency,
  series,
  showCosts,
  isStacked,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  currency: string;
  series: Array<ChartSeries>;
  showCosts: boolean;
  isStacked: boolean;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }
  const total = series.reduce((sum, item) => sum + point.balances[item.id].pax, 0);
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: 360 }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          {dayjs(point.day).format('D MMM YYYY')} · end of day
        </Typography>
        {isStacked && (
          <Typography variant="caption" fontWeight={600}>
            {total} pax
          </Typography>
        )}
      </Stack>
      <Stack spacing={0.25}>
        {series.map((item) => {
          const balance = point.balances[item.id];
          return (
            <SeriesLabel
              key={item.id}
              series={item}
              value={
                showCosts
                  ? `${balance.cost === null ? '—' : `${formatAmount(balance.cost)} ${currency}`} · ${balance.pax} pax`
                  : `${balance.pax} pax`
              }
            />
          );
        })}
      </Stack>
    </Paper>
  );
}

/** Up to MAX_TICKS days, evenly spread, always including the first and last. */
function spreadTicks(days: Array<number>): Array<number> {
  if (days.length <= MAX_TICKS) {
    return days;
  }
  const every = Math.ceil((days.length - 1) / (MAX_TICKS - 1));
  return days.filter((_day, index) => index % every === 0 || index === days.length - 1);
}

/**
 * End-of-day balances: supplier cost per tour/package configuration, or travellers - stacked by
 * the tour that sold them when an LTLE shares the departure.
 */
export default function CostPriceTrendChart({
  timeline,
  currency,
  snapshotDate,
  items,
  season,
  showCosts = true,
  shares = [],
  tourOfferId,
  order,
}: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const gridInk = isDark ? GRID.dark : GRID.light;
  const colourOf = useShareColour();

  // Bookings by the tour that sold them; cost is one band price for all, so it is not split.
  const split = useMemo<TourSplit | undefined>(
    () =>
      !showCosts && hasLinkedShare(shares)
        ? {
            keyOf: (entry) =>
              tourOfferId && entry.tourOfferId && entry.tourOfferId !== tourOfferId ? entry.tourOfferId : 'own',
            tours: shares.map((share) => ({
              key: share.tourOfferId ?? 'own',
              label: share.label,
              colour: colourOf(share),
            })),
          }
        : undefined,
    [showCosts, shares, tourOfferId, colourOf],
  );
  const { points, series, tourSeries } = useMemo(
    () => buildDailyCostPoints(timeline, items, currency, season, split, order),
    [timeline, items, currency, season, split, order],
  );
  const isStacked = tourSeries.length > 0;
  const colours = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
  ];
  const drawn: Array<ChartSeries> = isStacked
    ? tourSeries
    : series.map((configuration, index) => ({ ...configuration, colour: colours[index % colours.length] }));

  // A step holds its value until the next point, so the last day needs a point after it to show at all.
  const data = useMemo<Array<ChartPoint>>(() => {
    const days = points.map((point) => ({ ...point, day: point.time }));
    const last = days[days.length - 1];
    return last ? [...days, { ...last, time: dayjs(last.time).add(1, 'day').valueOf() }] : days;
  }, [points]);
  const ticks = useMemo(() => spreadTicks(points.map((point) => point.time)), [points]);

  if (points.length < 2) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough movements to show a trend yet.
      </Typography>
    );
  }

  const dataKey = (item: ChartSeries) => `balances.${item.id}.${showCosts ? 'cost' : 'pax'}`;

  return (
    <Stack spacing={1}>
      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={gridInk} vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={ticks}
              tickFormatter={(value) => dayjs(value).format('D MMM')}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              tickLine={false}
              stroke={gridInk}
            />
            <YAxis
              tickFormatter={(value) => (showCosts ? compact.format(value) : String(value))}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={showCosts ? 56 : 32}
              allowDecimals={showCosts}
              domain={showCosts ? ['auto', 'auto'] : [0, 'auto']}
            />
            <Tooltip
              content={<TrendTooltip currency={currency} series={drawn} showCosts={showCosts} isStacked={isStacked} />}
              cursor={{ stroke: AXIS_INK, strokeDasharray: '3 3' }}
            />
            {snapshotDate !== undefined && (
              <ReferenceLine
                x={dayjs(snapshotDate).startOf('day').valueOf()}
                stroke={theme.palette.text.secondary}
                strokeDasharray="4 4"
              />
            )}
            {drawn.map((item) =>
              isStacked ? (
                <Area
                  key={item.id}
                  name={item.label}
                  type="stepAfter"
                  dataKey={dataKey(item)}
                  stackId="pax"
                  stroke={item.colour}
                  fill={item.colour}
                  fillOpacity={0.2}
                  strokeWidth={2}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={item.id}
                  name={item.label}
                  type="stepAfter"
                  dataKey={dataKey(item)}
                  stroke={item.colour}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Stack direction="row" columnGap={2.5} rowGap={0.5} flexWrap="wrap" justifyContent="center" sx={{ px: 2 }}>
        {drawn.map((item) => (
          <Box key={item.id} sx={{ minWidth: 0, maxWidth: 360 }}>
            <SeriesLabel series={item} />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
